"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EventCard } from "@/components/EventCard";
import { fetchEvents } from "@/lib/api";
import type { CiudadVivaEvent } from "@/lib/events";
import { CheckCircle2, Clock, Loader2 } from "lucide-react";
import Link from "@/components/i18n/link";
import { JoinRankingCard } from "@/components/games/JoinRankingCard";
import rankingMessages from "@/components/games/ranking.i18n";
import { formatClock } from "@/lib/format";
import { ApiError } from "@/lib/api-client";
import { reportError } from "@/lib/client-log";
import { useCountry } from "@/lib/country-store";
import { useAuthStore } from "@/lib/store/auth-store";
import { LOCALE_META, type Locale } from "@/lib/i18n/config";
import { useLocale, useMessages } from "@/lib/i18n/client";
import { localizePath } from "@/lib/i18n/paths";
import { track } from "@/lib/tracking/track";
import { useCountdown } from "@/lib/use-countdown";
import { cn } from "@/lib/utils";
import {
  fetchWordSearchState,
  lineCells,
  loadWordSearchToken,
  saveWordSearchToken,
  sendWordSearchFind,
  startWordSearchLevel,
  type Cell,
  type PlayView,
  type WordEvent,
  type WordSearchState,
} from "@/lib/word-search";
import messages from "./page.i18n";

type T = (typeof messages)["es"];

const WORD_SEARCH_PATH = "/juegos/sopa-de-letras";
const DAILY_WORD_PATH = "/juegos/palabra-del-dia";
// El server cierra la partida 3 s después del límite (gracia para las jugadas
// en camino): se le vuelve a preguntar hasta que la cierre.
const SETTLE_RETRY_MS = 1600;

// Colores de las palabras encontradas (se repiten si hay más palabras).
const FOUND_COLORS = [
  "bg-emerald-500/35",
  "bg-sky-500/35",
  "bg-amber-500/40",
  "bg-fuchsia-500/30",
  "bg-lime-500/40",
  "bg-orange-500/35",
  "bg-violet-500/30",
  "bg-teal-500/35",
];

const key = (c: Cell) => `${c[0]},${c[1]}`;

function minutes(ms: number): number {
  return Math.round(ms / 60_000);
}

export function WordSearchGame() {
  const t = useMessages(messages);
  const lang = useLocale();
  const { country, loaded } = useCountry();
  const loggedIn = useAuthStore((s) => !!s.user);
  const authChecked = useAuthStore((s) => s.checked);
  const [state, setState] = useState<WordSearchState | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  // Los avisos se borran solos (como en la palabra del día).
  const noticeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const showNotice = useCallback((text: string) => {
    setNotice(text);
    clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(null), 2500);
  }, []);
  useEffect(() => () => clearTimeout(noticeTimer.current), []);
  // Eventos encontrados en esta partida (también los de la anterior terminada).
  const [events, setEvents] = useState<WordEvent[]>([]);
  // Nivel recién completado: se celebra con la sopa resuelta a la vista
  // antes de pasar a la pantalla siguiente.
  const [completed, setCompleted] = useState<PlayView | null>(null);
  // Partida (por su inicio) cuyo reloj ya llegó a cero.
  const [expired, setExpired] = useState<string | null>(null);
  const activeStartedAt = state?.active?.startedAt ?? null;
  const timeUp = activeStartedAt !== null && expired === activeStartedAt;
  // Comprobante sin sesión de la última respuesta (va en cada jugada).
  const tokenRef = useRef<string | undefined>(undefined);

  const apply = useCallback(
    (next: WordSearchState) => {
      if (!loggedIn) saveWordSearchToken(next.token);
      tokenRef.current = next.token;
      setState(next);
      const play = next.active ?? next.last;
      setEvents(
        (play?.words ?? [])
          .map((w) => w.event)
          .filter((e): e is WordEvent => !!e)
          .filter((e, i, all) => all.findIndex((x) => x.slug === e.slug) === i),
      );
    },
    [loggedIn],
  );

  const load = useCallback(() => {
    const local = loadWordSearchToken();
    fetchWordSearchState(loggedIn, { lang, country, token: local })
      .then((next) => {
        // Con sesión, las partidas de este navegador ya pasaron a la cuenta.
        if (loggedIn && local) saveWordSearchToken(undefined);
        apply(next);
        setLoadFailed(false);
      })
      .catch((err: unknown) => {
        reportError("word-search-state", err, { httpStatus: err instanceof ApiError ? err.status : undefined });
        setLoadFailed(true);
      });
  }, [lang, country, loggedIn, apply]);

  const fail = useCallback(
    (context: string, err: unknown) => {
      if (err instanceof ApiError && err.status === 409) {
        showNotice(t.conflict);
      } else if (err instanceof ApiError && err.code === "PLAYED_OTHER_LANGUAGE") {
        // Ya jugó hoy en otro idioma (otra pestaña): se muestra eso.
        load();
      } else {
        reportError(context, err, { httpStatus: err instanceof ApiError ? err.status : undefined });
        showNotice(err instanceof ApiError && err.status === 400 ? err.message : t.actionError);
      }
    },
    [t, load, showNotice],
  );


  useEffect(() => {
    // Recién con el país y la sesión resueltos (iniciar o cerrar sesión
    // vuelve a cargar las partidas que corresponden).
    if (loaded && authChecked) load();
  }, [loaded, authChecked, load]);

  const expire = useCallback(() => setExpired(activeStartedAt), [activeStartedAt]);

  // `state` cambia con cada respuesta: se repite hasta que deje de estar activa.
  useEffect(() => {
    if (!timeUp) return;
    const id = setTimeout(load, SETTLE_RETRY_MS);
    return () => clearTimeout(id);
  }, [timeUp, state, load]);

  const start = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    try {
      apply(await startWordSearchLevel(loggedIn, { lang, country, token: tokenRef.current }));
    } catch (err) {
      fail("word-search-start", err);
      load();
    } finally {
      setBusy(false);
    }
  }, [busy, loggedIn, lang, country, apply, fail, load]);

  const find = useCallback(
    async (from: Cell, to: Cell) => {
      try {
        const res = await sendWordSearchFind(loggedIn, { lang, country, token: tokenRef.current, from, to });
        apply(res);
        // Esta jugada encontró la última palabra.
        if (res.found && !res.active && res.last?.status === "won") setCompleted(res.last);
      } catch (err) {
        fail("word-search-find", err);
        load();
      }
    },
    [loggedIn, lang, country, apply, fail, load],
  );

  if (loadFailed) {
    return (
      <div className="mt-8 flex flex-col items-center gap-3 text-center">
        <p className="text-sm text-muted-foreground">{t.loadError}</p>
        <button
          type="button"
          onClick={load}
          className="inline-flex h-10 items-center rounded-full border border-border px-5 text-sm font-semibold hover:bg-muted"
        >
          {t.retryLoad}
        </button>
      </div>
    );
  }

  if (!state) {
    return <Loader2 className="mt-10 h-6 w-6 animate-spin text-muted-foreground" />;
  }

  return (
    <div className="mt-4 flex w-full max-w-5xl flex-col items-center gap-5">
      {!state.playedIn && <Progress state={state} t={t} />}
      {notice && (
        <p role="status" className="rounded-lg bg-muted px-3.5 py-2 text-sm">
          {notice}
        </p>
      )}
      {state.playedIn ? (
        <PlayedOtherLanguage playedIn={state.playedIn} t={t} />
      ) : completed ? (
        <Completed
          play={completed}
          events={events}
          loggedIn={loggedIn}
          onContinue={() => setCompleted(null)}
          t={t}
        />
      ) : state.active ? (
        timeUp ? (
          <TimeUp play={state.active} t={t} />
        ) : (
          <Board key={`${state.active.level}-${state.active.startedAt}`} play={state.active} onFind={find} onTimeUp={expire} t={t} />
        )
      ) : (
        <>
          <Between state={state} busy={busy} onStart={start} onNextDay={load} t={t} />
          {/* Debajo del cartel de victoria o derrota. */}
          {events.length > 0 && <EventsCarousel events={events} t={t} />}
        </>
      )}
      {events.length > 0 && state.active && !completed && <FoundEvents events={events} t={t} />}
      {!loggedIn && (
        <p className="text-center text-xs text-muted-foreground">
          {t.loginHint}{" "}
          <Link
            href={`/login?redirect=${encodeURIComponent(WORD_SEARCH_PATH)}`}
            className="font-semibold text-primary hover:underline"
          >
            {t.login}
          </Link>
        </p>
      )}
    </div>
  );
}

// Hoy ya jugó en otro idioma: una sopa al día (3 intentos en total), en
// cualquier idioma.
function PlayedOtherLanguage({ playedIn, t }: { playedIn: Locale; t: T }) {
  const language = LOCALE_META[playedIn].label;
  return (
    <div className="flex w-full max-w-md flex-col items-center gap-3 rounded-2xl border border-border bg-card p-6 text-center shadow-sm">
      <p className="font-semibold">{t.playedInTitle(language)}</p>
      <p className="text-sm text-muted-foreground">{t.playedInBody}</p>
      <a
        href={localizePath(WORD_SEARCH_PATH, playedIn)}
        className="inline-flex h-10 items-center justify-center rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
      >
        {t.playedInLink(language)}
      </a>
    </div>
  );
}

// El reloj llegó a cero: se avisa al instante, mientras el server cierra la
// partida (después sigue la pantalla entre partidas).
function TimeUp({ play, t }: { play: PlayView; t: T }) {
  const found = play.words.filter((w) => w.found).length;
  return (
    <div role="status" className="flex w-full max-w-md flex-col items-center gap-3 rounded-2xl border border-border bg-card p-6 text-center shadow-sm">
      <p className="font-semibold">{t.lostTitle}</p>
      <p className="text-sm text-muted-foreground">{t.lostBody(found, play.words.length)}</p>
      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden />
    </div>
  );
}

// Intentos que quedan (internamente "vidas") y niveles del día (los ganados
// con check).
function Progress({ state, t }: { state: WordSearchState; t: T }) {
  const current = state.active?.level ?? state.nextLevel;
  return (
    <div className="flex w-full flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-1" aria-label={t.lives(state.livesLeft)}>
        {Array.from({ length: state.maxLives }, (_, i) => (
          <span
            key={i}
            aria-hidden
            className={cn(
              "h-3 w-3 rounded-full border-2",
              i < state.livesLeft ? "border-primary bg-primary" : "border-muted-foreground/40",
            )}
          />
        ))}
        <span className="ml-1 text-sm text-muted-foreground">{t.lives(state.livesLeft)}</span>
      </div>
      <ol className="flex gap-1.5">
        {state.levels.map((l) => {
          const won = state.wonLevels.includes(l.level);
          return (
            <li
              key={l.level}
              className={cn(
                "inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium",
                won
                  ? "border-emerald-600/40 bg-emerald-600/10 text-emerald-700 dark:text-emerald-400"
                  : l.level === current
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border text-muted-foreground",
              )}
            >
              {won && <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />}
              {t.levelNames[l.level]}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

// Entre partidas: empezar el nivel que toca, resultado de la última, sin
// vidas o todo completado.
function Between({
  state,
  busy,
  onStart,
  onNextDay,
  t,
}: {
  state: WordSearchState;
  busy: boolean;
  onStart: () => void;
  onNextDay: () => void;
  t: T;
}) {
  const countdown = useCountdown(state.nextChangeAt, onNextDay).text;
  const level = state.nextLevel ? state.levels.find((l) => l.level === state.nextLevel) : null;
  const last = state.last;
  const lastName = last ? t.levelNames[last.level] : "";
  const foundInLast = last ? last.words.filter((w) => w.found).length : 0;

  return (
    <div className="flex w-full max-w-md flex-col items-center gap-4 rounded-2xl border border-border bg-card p-6 text-center shadow-sm">
      {last && (
        <div>
          {last.status === "won" ? (
            <>
              <p className="flex items-center justify-center gap-2 text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                <CheckCircle2 className="h-7 w-7" aria-hidden /> {t.completedTitle}
              </p>
              <p className="mt-1 font-medium">{t.completedBody(lastName)}</p>
              {last.timeMs !== undefined && (
                <p className="mt-1 text-sm text-muted-foreground">{t.completedTime(formatClock(last.timeMs))}</p>
              )}
            </>
          ) : (
            <p className="text-lg font-semibold">{t.lostTitle}</p>
          )}
          {last.status === "lost" && (
            <p className="mt-1 text-sm text-muted-foreground">{t.lostBody(foundInLast, last.words.length)}</p>
          )}
        </div>
      )}

      {!level ? (
        <div>
          <p className="font-semibold">{t.allDoneTitle}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t.allDoneBody}</p>
        </div>
      ) : state.livesLeft === 0 ? (
        <div>
          <p className="font-semibold">{t.noLivesTitle}</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {t.livesBack(countdown)}
          </p>
        </div>
      ) : (
        <>
          {last && (
            <p className="text-sm text-muted-foreground">
              {last.status === "won"
                ? t.nextUp(t.levelNames[level.level], t.lives(state.livesLeft))
                : t.retry(t.levelNames[level.level], t.lives(state.livesLeft))}
            </p>
          )}
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-primary">
              {t.levelNames[level.level]}
            </p>
            <p className="mt-1 text-sm">{t.levelSummary(level.words, minutes(level.timeLimitMs))}</p>
          </div>
          {!last && (
            <>
              <p className="text-sm text-muted-foreground">{t.howTo}</p>
              <p className="text-xs text-muted-foreground">{t.lifeRule}</p>
            </>
          )}
          <button
            type="button"
            onClick={onStart}
            disabled={busy}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-primary px-6 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {busy ? t.starting : t.start(t.levelNames[level.level])}
          </button>
        </>
      )}

      <div className="flex flex-wrap justify-center gap-x-5 gap-y-2">
        <RankingLink />
        {(!level || state.livesLeft === 0) && (
          <Link href={DAILY_WORD_PATH} className="text-sm font-semibold text-primary hover:underline">
            {t.otherGame}
          </Link>
        )}
      </div>
    </div>
  );
}

function RankingLink() {
  const tRanking = useMessages(rankingMessages);
  return (
    <Link href={`${WORD_SEARCH_PATH}/ranking`} className="text-sm font-semibold text-primary hover:underline">
      {tRanking.viewRanking}
    </Link>
  );
}

// La sopa en juego: grilla, reloj y lista de palabras. La selección se hace
// arrastrando (mouse o dedo) en línea recta; el server valida cada palabra.
function Board({
  play,
  onFind,
  onTimeUp,
  readOnly = false,
  t,
}: {
  play: PlayView;
  onFind?: (from: Cell, to: Cell) => Promise<void>;
  onTimeUp?: () => void;
  // Sopa ya terminada (se muestra resuelta, sin reloj ni selección).
  readOnly?: boolean;
  t: T;
}) {
  const { secondsLeft, text } = useCountdown(play.deadline, onTimeUp ?? noop, { short: true });
  const grid = useMemo(() => play.rows.map((r) => Array.from(r)), [play.rows]);
  const size = play.size;
  const [anchor, setAnchor] = useState<Cell | null>(null);
  const [hover, setHover] = useState<Cell | null>(null);
  const [pending, setPending] = useState<Cell[] | null>(null);
  const boardRef = useRef<HTMLDivElement>(null);

  // Casilla → color de la palabra encontrada que la cubre.
  const foundColor = useMemo(() => {
    const map = new Map<string, string>();
    play.found.forEach((f, i) => {
      for (const c of lineCells(f.from, f.to)) map.set(key(c), FOUND_COLORS[i % FOUND_COLORS.length]);
    });
    return map;
  }, [play.found]);

  const selection = useMemo(() => {
    if (!anchor || !hover) return pending ?? [];
    return lineCells(anchor, hover).filter(([r, c]) => r >= 0 && r < size && c >= 0 && c < size);
  }, [anchor, hover, pending, size]);
  const selected = useMemo(() => new Set(selection.map(key)), [selection]);

  function cellAt(x: number, y: number): Cell | null {
    const el = document.elementFromPoint(x, y)?.closest<HTMLElement>("[data-cell]");
    if (!el || !boardRef.current?.contains(el)) return null;
    const [r, c] = el.dataset.cell!.split(",").map(Number);
    return [r, c];
  }

  function onPointerDown(e: React.PointerEvent) {
    if (readOnly) return;
    const cell = cellAt(e.clientX, e.clientY);
    if (!cell) return;
    e.preventDefault();
    boardRef.current?.setPointerCapture(e.pointerId);
    setPending(null);
    setAnchor(cell);
    setHover(cell);
  }

  function onPointerMove(e: React.PointerEvent) {
    if (!anchor) return;
    const cell = cellAt(e.clientX, e.clientY);
    if (cell && key(cell) !== key(hover ?? cell)) setHover(cell);
    else if (cell && !hover) setHover(cell);
  }

  function onPointerUp() {
    if (!anchor || !hover) return;
    const cells = selection;
    setAnchor(null);
    setHover(null);
    if (cells.length < 2) return;
    // La selección queda marcada mientras responde el server.
    setPending(cells);
    void onFind?.(cells[0], cells[cells.length - 1]).finally(() => setPending(null));
  }

  const pendingWords = play.words.filter((w) => !w.found);
  const foundWords = play.words.filter((w) => w.found);
  const urgent = !readOnly && secondsLeft <= 60;

  return (
    <div className="flex w-full flex-col items-center gap-4 lg:flex-row lg:items-start lg:justify-center">
      <div className="flex w-full max-w-[min(100%,560px)] flex-col gap-2">
        <div className="flex items-center justify-between text-sm">
          {readOnly ? (
            <span className="inline-flex items-center gap-1.5 font-semibold text-emerald-600 dark:text-emerald-400">
              <CheckCircle2 className="h-4 w-4" aria-hidden /> {t.completedTitle}
            </span>
          ) : (
            <span
              className={cn("inline-flex items-center gap-1.5 font-mono font-semibold tabular-nums", urgent && "text-rose-600")}
              aria-label={t.timeLeft}
            >
              <Clock className="h-4 w-4" aria-hidden /> {text}
            </span>
          )}
          <span className="font-semibold tabular-nums">{t.found(foundWords.length, play.words.length)}</span>
        </div>
        <div
          ref={boardRef}
          role="grid"
          aria-label={t.gridLabel}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={() => {
            setAnchor(null);
            setHover(null);
          }}
          className="grid touch-none select-none gap-0.5 rounded-xl border border-border bg-card p-1.5"
          style={{ gridTemplateColumns: `repeat(${size}, minmax(0, 1fr))` }}
        >
          {grid.map((row, r) =>
            row.map((letter, c) => {
              const k = `${r},${c}`;
              return (
                <span
                  key={k}
                  role="gridcell"
                  data-cell={k}
                  className={cn(
                    "flex aspect-square items-center justify-center rounded-md font-semibold uppercase leading-none transition-colors",
                    size >= 15 ? "text-[13px] sm:text-base" : size >= 14 ? "text-sm sm:text-base" : "text-base sm:text-lg",
                    selected.has(k)
                      ? "bg-primary text-primary-foreground"
                      : foundColor.get(k) ?? "hover:bg-muted",
                  )}
                >
                  {letter}
                </span>
              );
            }),
          )}
        </div>
      </div>

      <div className="flex w-full max-w-[min(100%,560px)] flex-col gap-4 lg:w-auto lg:max-w-none">
        {pendingWords.length > 0 && <WordList title={t.wordsTitle} words={pendingWords.map((w) => w.display)} />}
        {foundWords.length > 0 && (
          <WordList title={t.foundTitle} words={foundWords.map((w) => w.display)} found />
        )}
      </div>
    </div>
  );
}

function noop(): void {}

// Palabras por columna en pantallas anchas: si hay más, se agrega otra
// columna (en el difícil son 30 y en una sola se salían de la pantalla). En
// celular van como chips que se acomodan solos.
const WORDS_PER_COLUMN = 10;

function WordList({ title, words, found = false }: { title: string; words: string[]; found?: boolean }) {
  const rows = Math.min(words.length, WORDS_PER_COLUMN);
  return (
    <div>
      <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
        {title} <span className="tabular-nums">({words.length})</span>
      </p>
      <ul
        className="flex flex-wrap gap-1.5 lg:grid lg:auto-cols-max lg:grid-flow-col lg:gap-x-3 lg:gap-y-1.5 lg:[grid-template-rows:repeat(var(--rows),auto)]"
        style={{ "--rows": rows } as React.CSSProperties}
      >
        {words.map((w) => (
          <li
            key={w}
            className={cn(
              "rounded-full border px-2.5 py-0.5 text-xs font-medium lg:rounded-md",
              found
                ? "border-transparent bg-emerald-600/10 text-emerald-700 line-through dark:text-emerald-400"
                : "border-border",
            )}
          >
            {w}
          </li>
        ))}
      </ul>
    </div>
  );
}

// Celebración al encontrar la última palabra: se nota que terminó, con la
// sopa resuelta, antes de pasar a la pantalla del siguiente nivel.
function Completed({
  play,
  events,
  loggedIn,
  onContinue,
  t,
}: {
  play: PlayView;
  events: WordEvent[];
  loggedIn: boolean;
  onContinue: () => void;
  t: T;
}) {
  return (
    <div className="flex w-full flex-col items-center gap-5">
      <div className="w-full max-w-md rounded-2xl border border-emerald-600/30 bg-emerald-600/10 p-6 text-center">
        <p className="flex items-center justify-center gap-2 text-3xl font-bold text-emerald-700 dark:text-emerald-400">
          <CheckCircle2 className="h-8 w-8" aria-hidden /> {t.completedTitle}
        </p>
        <p className="mt-2 font-medium">{t.completedBody(t.levelNames[play.level])}</p>
        {play.timeMs !== undefined && (
          <p className="mt-1 text-sm text-muted-foreground">{t.completedTime(formatClock(play.timeMs))}</p>
        )}
        {/* Con sesión, una sola vez: cómo se vería en el ranking. */}
        {loggedIn && (
          <div className="mt-4">
            <JoinRankingCard />
          </div>
        )}
        <button
          type="button"
          onClick={onContinue}
          className="mt-4 inline-flex h-11 items-center justify-center rounded-full bg-primary px-6 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
        >
          {t.continue}
        </button>
        <div className="mt-3">
          <RankingLink />
        </div>
      </div>
      {events.length > 0 && <EventsCarousel events={events} t={t} />}
      <Board play={play} readOnly t={t} />
    </div>
  );
}

// Al terminar: carrusel con los eventos de donde salieron las palabras
// encontradas (las mismas cards del resto del sitio). Se piden en el
// navegador, directo al backend: la página es estática.
function EventsCarousel({ events, t }: { events: WordEvent[]; t: T }) {
  const lang = useLocale();
  const slugsKey = events.map((e) => e.slug).join(",");
  const [result, setResult] = useState<{ key: string; events: CiudadVivaEvent[] } | null>(null);

  useEffect(() => {
    if (!slugsKey) return;
    let cancelled = false;
    const slugs = slugsKey.split(",");
    fetchEvents({ slugs, limit: Math.min(slugs.length, 50), lang })
      .then((res) => {
        // En el orden en que se encontraron las palabras.
        const bySlug = new Map(res.data.map((e) => [e.slug, e]));
        const ordered = slugs.map((s) => bySlug.get(s)).filter((e): e is CiudadVivaEvent => !!e);
        if (!cancelled) setResult({ key: slugsKey, events: ordered });
      })
      .catch((err) => {
        // El carrusel es un extra: si falla, no se muestra (queda registrado).
        reportError("word-search-events", err, { detail: `slugs=${slugsKey}` });
        if (!cancelled) setResult({ key: slugsKey, events: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [slugsKey, lang]);

  if (!result || result.key !== slugsKey || result.events.length === 0) return null;

  return (
    <section className="w-full">
      <div className="mb-3">
        <h2 className="text-lg font-semibold tracking-tight">{t.carouselTitle}</h2>
        <p className="text-sm text-muted-foreground">{t.carouselSubtitle}</p>
      </div>
      {/* Carrusel horizontal en todas las pantallas (pueden ser hasta 30). */}
      <div className="-mx-5 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-2 sm:mx-0 sm:px-0">
        {result.events.map((e, i) => (
          <div key={e.slug} className="w-[80%] shrink-0 snap-start sm:w-72">
            <EventCard event={e} position={i + 1} />
          </div>
        ))}
      </div>
    </section>
  );
}

// Eventos de donde salieron las palabras encontradas: el link a salir.
function FoundEvents({ events, t }: { events: WordEvent[]; t: T }) {
  return (
    <div className="w-full max-w-xl">
      <p className="text-center text-sm font-semibold">{t.eventsTitle}</p>
      <p className="mb-2 text-center text-xs text-muted-foreground">{t.eventsHint}</p>
      <ul className="flex flex-wrap justify-center gap-2">
        {events.map((e) => (
          <li key={e.slug}>
            <Link
              href={`/evento/${e.slug}`}
              target="_blank"
              onClick={() => track("event_card_click", { slug: e.slug, list: "word_search" })}
              className="inline-flex max-w-[18rem] items-center rounded-full border border-border px-3 py-1.5 text-sm transition hover:border-foreground/30"
            >
              <span className="truncate">{e.title}</span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
